import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColDef } from 'ag-grid-community';
import { ArrowLeft, Loader2, Building2, Phone, Mail, MapPin, BookOpen, FileText, Calendar, DollarSign, MoreHorizontal, ClipboardList, ExternalLink, ScrollText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { 
  vendorService, 
  invoiceService, 
  invoiceItemService, 
  profileService,
  priceBookService,
  priceBookItemService,
  externalSystemService,
  quoteService,
  type ServiceContext 
} from '@/services';
import RecordMetadata from '@/components/RecordMetadata';
import ExternalSystemInfo from '@/components/ExternalSystemInfo';
import { EntityStatusActions } from '@/components/status/EntityStatusActions';
import { usePagination } from '@/hooks/usePagination';
import { AGDataGrid } from '@/components/ui/ag-data-grid';
import type { QuoteListRow } from '@/services';
import { parseISO, format } from 'date-fns';

export default function VendorDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [mapDialogOpen, setMapDialogOpen] = useState(false);

  // Pagination for Quotes tab
  const quotesPagination = usePagination({
    storageKey: 'vendor_detail_quotes',
    persistToUrl: false,
  });

  // Reset page index when vendor changes
  useEffect(() => {
    quotesPagination.resetToFirstPage();
  }, [id]);
  // Fetch vendor details using service
  const { data: vendor, isLoading } = useQuery({
    queryKey: ['vendor', id],
    queryFn: async () => {
      const result = await vendorService.getWithAuditNames(id!);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!id,
  });

  // Fetch price books for this vendor using service
  const { data: priceBooks = [], isLoading: priceBooksLoading } = useQuery({
    queryKey: ['vendor-price-books', id],
    queryFn: async () => {
      const result = await priceBookService.listByVendorWithRelations(id!);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!id,
  });

  // Fetch item counts for price books using service
  const { data: priceBookItemCounts = {} } = useQuery({
    queryKey: ['vendor-price-book-item-counts', id],
    queryFn: async () => {
      const bookIds = priceBooks.map(b => b.id);
      if (bookIds.length === 0) return {};
      
      const result = await priceBookItemService.countByPriceBooks(bookIds);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: priceBooks.length > 0,
  });

  // Fetch invoices for this vendor using service
  const { data: invoices = [], isLoading: invoicesLoading, refetch: refetchInvoices } = useQuery({
    queryKey: ['vendor-invoices', id],
    queryFn: async () => {
      const result = await invoiceService.listByVendor(id!);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!id,
  });

  // Fetch item counts for invoices using service
  const { data: invoiceItemCounts = {} } = useQuery({
    queryKey: ['vendor-invoice-item-counts', id],
    queryFn: async () => {
      const invoiceIds = invoices.map(inv => inv.id);
      if (invoiceIds.length === 0) return {};

      const result = await invoiceItemService.countByInvoices(invoiceIds);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: invoices.length > 0,
  });

  // Get user profile for company_id
  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const result = await profileService.get(user.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  // Lookup external system ID for linking
  const { data: externalSystemDetails } = useQuery({
    queryKey: ['external-system-by-name', vendor?.external_system, profile?.company_id],
    queryFn: async () => {
      if (!vendor?.external_system || !profile?.company_id) return null;
      const result = await externalSystemService.getByName(vendor.external_system, profile.company_id);
      if (result.error) return null;
      return result.data;
    },
    enabled: !!vendor?.external_system && !!profile?.company_id,
  });

  // Fetch list of external systems for edit dropdown
  const { data: externalSystems = [] } = useQuery({
    queryKey: ['external-systems', profile?.company_id],
    queryFn: async () => {
      const result = await externalSystemService.list(profile!.company_id!);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!profile?.company_id,
  });

  // Fetch quotes for this vendor (paginated)
  const { data: quotesResult, isLoading: quotesLoading, isFetching: quotesFetching } = useQuery({
    queryKey: ['vendor-quotes', id, quotesPagination.pageIndex, quotesPagination.pageSize, quotesPagination.debouncedSearch],
    queryFn: async () => {
      const result = await quoteService.listByVendorPaginated(
        id!,
        { page: quotesPagination.pageIndex, pageSize: quotesPagination.pageSize },
        { search: quotesPagination.debouncedSearch || undefined }
      );
      if (result.error) throw result.error;
      return result;
    },
    enabled: !!id,
  });

  // Quote column definitions
  const quoteColumnDefs = useMemo<ColDef<QuoteListRow>[]>(() => [
    { headerName: 'Well Name', field: 'well_name', flex: 1.5, valueFormatter: (p) => p.value || '—' },
    {
      headerName: 'Status',
      width: 140,
      sortable: true,
      valueGetter: (params) => params.data?.status_name,
      cellRenderer: (params: any) =>
        params.value ? (
          <span className="badge badge-primary">{params.value}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      headerName: 'Effective Date', field: 'effective_date', flex: 1,
      valueFormatter: (p) => p.value ? format(parseISO(p.value), 'MM/dd/yyyy') : '—',
    },
    {
      headerName: 'Expiration Date', field: 'expiration_date', flex: 1,
      valueFormatter: (p) => p.value ? format(parseISO(p.value), 'MM/dd/yyyy') : '—',
    },
    { headerName: 'Version', field: 'version_number', flex: 0.6, type: 'rightAligned', valueFormatter: (p) => p.value ?? '—' },
    { headerName: 'Quote #', field: 'vendor_quote_number', flex: 1, valueFormatter: (p) => p.value || '—' },
    {
      headerName: 'Price', field: 'total_price', flex: 1, type: 'rightAligned',
      valueFormatter: (p) => p.value != null
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(p.value)
        : '—',
    },
    {
      headerName: 'Modified', field: 'quote_modified', flex: 1.2,
      valueFormatter: (p) => p.value ? format(parseISO(p.value), 'MM/dd/yyyy hh:mm a') : '—',
    },
  ], []);

  // Handler for saving external system changes
  const handleSaveExternalSystem = async (system: string | null, recordId: string | null) => {
    if (!vendor || !profile?.company_id || !user) return;

    const context: ServiceContext = {
      companyId: profile.company_id,
      userId: user.id,
    };

    const snapshot = {
      external_system: vendor.external_system,
      external_system_record_id: vendor.external_system_record_id,
    };

    const result = await vendorService.update(
      id!,
      { external_system: system, external_system_record_id: recordId },
      snapshot,
      context
    );

    if (result.error) {
      toast({ title: 'Error', description: 'Failed to update external system link.', variant: 'destructive' });
      throw result.error;
    }

    queryClient.invalidateQueries({ queryKey: ['vendor', id] });
    queryClient.invalidateQueries({ queryKey: ['external-system-by-name', system, profile.company_id] });
    toast({ title: 'Updated', description: 'External system link has been updated.' });
  };

  // Update vendor status_id with history tracking via service layer
  const updateStatusId = useMutation({
    mutationFn: async ({ statusId, comments }: { statusId: string; comments?: string }) => {
      if (!vendor || !profile?.company_id || !user) throw new Error('Missing required data');

      const context: ServiceContext = {
        companyId: profile.company_id,
        userId: user.id,
      };

      const result = await vendorService.updateStatus(
        id!,
        statusId,
        vendor.status_id,
        context,
        { comments }
      );

      if (result.error) throw result.error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor', id] });
      toast({ title: 'Status updated', description: 'Vendor status has been updated.' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to update status.', variant: 'destructive' });
    },
  });

  const formatAddress = () => {
    if (!vendor) return null;
    const parts = [
      vendor.address,
      vendor.city,
      vendor.state,
      vendor.country,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  const getGoogleMapsUrl = () => {
    const address = formatAddress();
    if (!address) return null;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  const getGoogleMapsEmbedUrl = () => {
    const address = formatAddress();
    if (!address) return null;
    return `https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString();
  };

  const getPriceBookStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'badge-success',
      draft: 'badge-warning',
      archived: 'badge-destructive',
    };
    return styles[status] || 'badge-primary';
  };

  const getInvoiceStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      draft: 'badge-secondary',
      pending_review: 'badge-warning',
      approved: 'badge-primary',
      paid: 'badge-success',
      disputed: 'badge-destructive',
    };
    return styles[status] || 'badge-secondary';
  };

  const formatStatus = (status: string) => {
    return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const handleDeleteInvoice = async (invoiceId: string) => {
    if (!profile?.company_id || !user?.id) return;
    
    const context: ServiceContext = { companyId: profile.company_id, userId: user.id };
    
    // Find the invoice to get snapshot for history
    const invoice = invoices.find(i => i.id === invoiceId);
    
    // First delete invoice items using service
    const itemsResult = await invoiceItemService.deleteByInvoice(invoiceId, context);
    if (itemsResult.error) {
      toast({ title: 'Error', description: 'Failed to delete invoice items', variant: 'destructive' });
      return;
    }

    // Then delete the invoice using service
    const invoiceResult = await invoiceService.delete(
      invoiceId,
      invoice as unknown as Record<string, unknown>,
      context
    );

    if (invoiceResult.error) {
      toast({ title: 'Error', description: 'Failed to delete invoice', variant: 'destructive' });
    } else {
      toast({ title: 'Deleted', description: 'Invoice deleted successfully' });
      refetchInvoices();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">Vendor not found.</p>
        <Button variant="outline" onClick={() => navigate('/vendors')}>
          Back to Vendors
        </Button>
      </div>
    );
  }

  return (
    <div className="animate-slide-up">
      {/* Back button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/vendors')}
        className="mb-4 -ml-2"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Vendors
      </Button>

      {/* Vendor Header + Status Actions */}
      <div className="flex flex-col lg:flex-row gap-6 mb-6">
        {/* Vendor Header - 70% */}
        <div className="bg-card border border-border rounded-lg p-6 lg:w-[70%]">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-7 h-7 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-foreground mb-2">{vendor.name}</h1>
              
              <div className="space-y-1">
                {vendor.phone && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="w-4 h-4" />
                    <span>{vendor.phone}</span>
                  </div>
                )}
                {vendor.email && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Mail className="w-4 h-4" />
                    <a href={`mailto:${vendor.email}`} className="text-primary hover:underline">
                      {vendor.email}
                    </a>
                  </div>
                )}
                {formatAddress() && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="w-4 h-4" />
                    <button
                      onClick={() => setMapDialogOpen(true)}
                      className="text-primary hover:underline cursor-pointer text-left"
                    >
                      {formatAddress()}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Status Actions - 30% */}
        <div className="bg-card border border-border rounded-lg p-6 lg:w-[30%]">
          <EntityStatusActions
            entityType="vendor"
            currentStatusId={vendor.status_id}
            onStatusChange={(statusId, _statusName, comments) => 
              updateStatusId.mutate({ statusId, comments })
            }
          />
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="quotes" className="mb-6">
        <TabsList className="w-full justify-start border-b border-border rounded-none bg-transparent h-auto p-0 mb-6">
          <TabsTrigger value="quotes" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3 flex items-center gap-2">
            <ScrollText className="w-4 h-4" />
            Quotes
          </TabsTrigger>
          <TabsTrigger value="price-books" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3 flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            Price Books
          </TabsTrigger>
          <TabsTrigger value="invoices" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Invoices
          </TabsTrigger>
          <TabsTrigger value="field-tickets" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3 flex items-center gap-2">
            <ClipboardList className="w-4 h-4" />
            Field Tickets
          </TabsTrigger>
        </TabsList>

        {/* Quotes Tab */}
        <TabsContent value="quotes" className="mt-4">
          <AGDataGrid<QuoteListRow>
            rowData={quotesResult?.data ?? []}
            columnDefs={quoteColumnDefs}
            totalCount={quotesResult?.totalCount ?? 0}
            pageIndex={quotesPagination.pageIndex}
            pageSize={quotesPagination.pageSize}
            pageSizeOptions={quotesPagination.pageSizeOptions}
            searchValue={quotesPagination.searchValue}
            searchPlaceholder="Search well name, API, or quote #..."
            isLoading={quotesLoading}
            isFetching={quotesFetching}
            onSearchChange={quotesPagination.setSearchValue}
            onPageChange={quotesPagination.setPageIndex}
            onPageSizeChange={quotesPagination.setPageSize}
            onRowClicked={(row) => row.quote_id && navigate(`/quotes/${row.quote_id}`)}
            height="calc(100vh - 520px)"
          />
        </TabsContent>

        <TabsContent value="price-books" className="mt-4">
          {priceBooksLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : priceBooks.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No price books found for this vendor.
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Region</th>
                    <th>Effective Date</th>
                    <th>Expiry Date</th>
                    <th>Items</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {priceBooks.map((book) => (
                    <tr 
                      key={book.id} 
                      className="cursor-pointer" 
                      onClick={() => navigate(`/price-books/${book.id}`)}
                    >
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <BookOpen className="w-5 h-5 text-primary" />
                          </div>
                          <span className="font-medium">{book.name}</span>
                        </div>
                      </td>
                      <td className="text-muted-foreground">
                        {book.list_regions?.display_value || '—'}
                      </td>
                      <td>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          {formatDate(book.effective_date)}
                        </div>
                      </td>
                      <td>{formatDate(book.expiry_date)}</td>
                      <td>{priceBookItemCounts[book.id] || 0}</td>
                      <td>
                        <span className={`badge ${getPriceBookStatusBadge((book as any).current_status?.name?.toLowerCase() || 'unknown')}`}>
                          {(book as any).current_status?.name || 'Unknown'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="mt-4">
          {invoicesLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No invoices found for this vendor.
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Amount</th>
                    <th>Items</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th className="w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr 
                      key={invoice.id} 
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/invoices/${invoice.id}`)}
                    >
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-primary" />
                          </div>
                          <span className="font-medium">{invoice.invoice_number || invoice.id.slice(0, 8)}</span>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-1 font-medium">
                          <DollarSign className="w-4 h-4 text-muted-foreground" />
                          {(invoice.total_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </div>
                      </td>
                      <td>{invoiceItemCounts[invoice.id] || 0} items</td>
                      <td className="text-muted-foreground">
                        {invoice.invoice_date
                          ? new Date(invoice.invoice_date).toLocaleDateString()
                          : new Date(invoice.created).toLocaleDateString()}
                      </td>
                      <td>
                        <span className={`badge ${getInvoiceStatusBadge((invoice as any).current_status?.name?.toLowerCase() || 'unknown')}`}>
                          {(invoice as any).current_status?.name || 'Unknown'}
                        </span>
                      </td>
                      <td className="text-muted-foreground">
                        <div className="flex flex-col">
                          <span>{new Date(invoice.created).toLocaleDateString()}</span>
                          <span className="text-xs">{new Date(invoice.created).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="p-2">
                              <MoreHorizontal className="w-5 h-5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/invoices/${invoice.id}`)}>
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-destructive"
                              onClick={() => handleDeleteInvoice(invoice.id)}
                            >
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {/* Field Tickets Tab */}
        <TabsContent value="field-tickets" className="mt-4">
          <div className="text-center py-12 text-muted-foreground">
            Field tickets tab coming soon.
          </div>
        </TabsContent>
      </Tabs>

      {/* Metadata and External System Info Panels */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <RecordMetadata record={vendor} />
        <ExternalSystemInfo
          externalSystem={vendor.external_system}
          externalSystemRecordId={vendor.external_system_record_id}
          externalSystemId={externalSystemDetails?.id}
          availableSystems={externalSystems.map(s => ({ id: s.id, name: s.name }))}
          onSave={handleSaveExternalSystem}
        />
      </div>

      {/* Google Maps Dialog */}
      <Dialog open={mapDialogOpen} onOpenChange={setMapDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Vendor Location</DialogTitle>
          </DialogHeader>
          <div className="aspect-video w-full rounded-lg overflow-hidden border border-border">
            {getGoogleMapsEmbedUrl() && (
              <iframe
                src={getGoogleMapsEmbedUrl()!}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Vendor location on Google Maps"
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMapDialogOpen(false)}>
              Close
            </Button>
            <Button onClick={() => window.open(getGoogleMapsUrl()!, '_blank')}>
              <ExternalLink className="w-4 h-4 mr-2" />
              Open in Google Maps
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
